<!-- <script lang="ts"> -->
<script lang="ts" setup>
import '@wangeditor-next/editor/dist/css/style.css';
import { onBeforeUnmount, onMounted, ref, shallowRef } from 'vue';
import { Editor, Toolbar } from '@wangeditor-next/editor-for-vue';
import type { IEditorConfig, IToolbarConfig } from '@wangeditor-next/editor';
import { axiosInstance } from '@sa/axios';
import { findAll as findAllAttachmentType } from '@/service/api/base/biz-attachment-type';
import { generatePresignedUrl, saveRemoteFile } from '@/service/api/base/file';
import { addFilter } from '@/utils/common';
import { getUrl } from '@/utils/attachmentUtil';
import type { Api } from '@/typings/api';

export type UploadParam = {
  id: string;
  typeCode: string;
  entityName: string;
};
export interface WangEditorProps {
  /** 下拉选项值 */
  placeholder?: string;
  /** 只读 */
  readonly?: boolean;
  /** 禁用 */
  disabled?: boolean;
  excludeKeys: string[];
  /** 上传参数 */
  uploadParam?: UploadParam;
}
defineOptions({
  name: 'WangEditor'
});
const props = withDefaults(defineProps<WangEditorProps>(), {
  placeholder: '请输入内容...',
  /** 禁用 */
  disabled: false,
  /** 只读 */
  readonly: false,
  /** 上传参数 */
  uploadParam: undefined
});

const showEditor = ref(false);
// 编辑器实例，必须用 shallowRef
const editorRef = shallowRef();
const mode = ref<string>('default');

const attachmentType = ref<Api.Base.BizAttachmentType>();

// 内容 HTML
// const valueHtml = ref('<p>hello</p>');
const toolbarConfig: Partial<IToolbarConfig> = {};
const editorConfig: Partial<IEditorConfig> = {};
const valueHtml = defineModel<string>('value');
toolbarConfig.excludeKeys = props.excludeKeys;

function initEditor() {
  editorConfig.placeholder = props.placeholder;
  editorConfig.readOnly = props.readonly || props.disabled;
  editorConfig.MENU_CONF ??= {};
  if (props.uploadParam) {
    editorConfig.MENU_CONF.uploadImage = {
      server: '',
      metaWithUrl: false,
      onSuccess: () => {},
      onFailed: () => {},
      onError: () => {},
      base64LimitSize: 0,
      maxFileSize: 5 * 1024 * 1024,
      maxNumberOfFiles: attachmentType.value!.maxSize,
      allowedFileTypes: attachmentType.value!.accept.split(','),
      // 自定义上传
      async customUpload(file: File, insertFn: any) {
        generatePresignedUrl({
          objectId: props.uploadParam!.id,
          bizAttachmentType: attachmentType.value!.id,
          mime: file.type ?? '',
          filename: file.name
        })
          .then(async res => {
            if (res.data?.url) {
              const uploadResult = res.data;
              const response = await axiosInstance.put(uploadResult.url, file, {
                headers: uploadResult.headers
                // onUploadProgress: progressEvent => {
                //   onProgress({
                //     percent:
                //       (progressEvent.progress ?? progressEvent.loaded / (progressEvent.total ?? file.file!.size)) * 100
                //   });
                // }
              });
              if (response.status === 200) {
                const fileResult = await saveRemoteFile({
                  bizAttachmentType: attachmentType.value!.id,
                  filename: uploadResult.filename
                });
                window.$message?.success('上传成功');
                // uploadAttachments.value[file.id] = fileResult.data;
                // onFinish();
                insertFn(await getUrl(fileResult.data));
              } else {
                console.warn('🚀 ~ handleUpload ~ response:', response);
                window.$message?.error('上传失败');
                // onError();
              }
            }
          })
          .catch(error => {
            window.$message?.error(error.message);
            // onError();
          });
      }
    };
  }
  showEditor.value = true;
}

// 组件销毁时，也及时销毁编辑器
onBeforeUnmount(() => {
  const editor = editorRef.value;
  if (!editor || editor === null) return;
  editor.destroy();
});

/** 获取EntityName对应所有附件类型 */
async function getAttachmentTypes(uploadParam: UploadParam) {
  const params: Api.Common.CommonSearchParams = {
    column: ['id', 'typeCode', 'typeName', 'accept', 'maxSize'],
    filter: [
      {
        column: 'genEntityMain.entityName',
        value: uploadParam.entityName,
        type: 'andEq'
      }
    ]
  };
  if (uploadParam.typeCode && uploadParam.typeCode.length > 0) {
    addFilter(params.filter, {
      column: 'typeCode',
      value: uploadParam.typeCode,
      type: 'andEq'
    });
  }
  const { data } = await findAllAttachmentType(params);
  if (data?.content?.length) {
    attachmentType.value = data.content[0];
  } else {
    // eslint-disable-next-line no-console
    console.error('附件类型配置错误：', uploadParam.typeCode);
  }
}

const handleCreated = (editor: any) => {
  editorRef.value = editor; // 记录 editor 实例，重要！
};

onMounted(async () => {
  if (props.uploadParam) {
    await getAttachmentTypes(props.uploadParam);
    initEditor();
  } else {
    initEditor();
  }
});
</script>

<template>
  <div class="content">
    <Toolbar
      v-if="!(readonly || disabled)"
      class="toolbar"
      :editor="editorRef"
      :default-config="toolbarConfig"
      :mode="mode"
    />
    <Editor
      v-if="showEditor"
      v-model="valueHtml"
      class="editor"
      :default-config="editorConfig"
      :mode="mode"
      @on-created="handleCreated"
    />
  </div>
</template>

<style scoped>
.content {
  width: 100%;
  border: 1px solid #ccc;
}

.toolbar {
  border-bottom: 1px solid #ccc;
}

.editor {
  min-height: 500px;
  max-height: 800px;
}
</style>
